import React, { useEffect, useMemo, useState } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import {
  Paper,
  Box,
  Typography,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  CircularProgress,
} from '@mui/material';
import { MaterialReactTable, type MRT_ColumnDef } from 'material-react-table';
import { AdminSmallBusinessService, SmallBusiness } from 'us-web-services';
import PageStyles from 'src/styles/PageStyles';

function statusColor(status: string): 'success' | 'warning' | 'error' | 'default' {
  switch (status) {
    case 'active':
    case 'trialing':
      return 'success';
    case 'past_due':
    case 'incomplete':
      return 'warning';
    case 'canceled':
    case 'unpaid':
      return 'error';
    default:
      return 'default';
  }
}

function formatMoney(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function formatDate(value: string | null): string {
  if (!value) return '—';

  return new Date(value).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function SmallBusinessLookup({ history }: RouteComponentProps) {
  const { classes } = PageStyles();
  const [rows, setRows] = useState<SmallBusiness[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [tierFilter, setTierFilter] = useState<string>('');

  useEffect(() => {
    let cancelled = false;

    const fetchRows = async () => {
      setLoading(true);
      try {
        const response = await AdminSmallBusinessService.findByFilter({
          status: statusFilter || undefined,
          tier: tierFilter || undefined,
          limit: 100,
        });

        if (!cancelled && response?.data) {
          setRows(response.data.data ?? []);
          setTotal(response.data.meta?.size ?? 0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchRows();

    return () => {
      cancelled = true;
    };
  }, [statusFilter, tierFilter]);

  const columns = useMemo<MRT_ColumnDef<SmallBusiness>[]>(
    () => [
      {
        header: 'Company',
        accessorKey: 'companyName',
        Cell: ({ row }) => (
          <a
            href={`/companies/${row.original.companyId}`}
            onClick={(e) => {
              e.preventDefault();
              history.push(`/companies/${row.original.companyId}`);
            }}
          >
            {row.original.companyName}
          </a>
        ),
      },
      { header: 'Tier', accessorKey: 'tier' },
      { header: 'Interval', accessorKey: 'billingInterval' },
      {
        header: 'Status',
        accessorKey: 'status',
        Cell: ({ cell }) => (
          <Chip
            size='small'
            color={statusColor(cell.getValue<string>())}
            label={cell.getValue<string>().replace('_', ' ')}
          />
        ),
      },
      {
        header: 'Seats',
        accessorFn: (row) => `${row.seatsUsed} / ${row.seatLimit}`,
        id: 'seats',
      },
      {
        header: 'MRR',
        accessorFn: (row) => formatMoney(row.mrrCents),
        id: 'mrr',
      },
      {
        header: 'Next bill',
        accessorFn: (row) => formatDate(row.currentPeriodEnd),
        id: 'currentPeriodEnd',
      },
      {
        header: 'Created',
        accessorFn: (row) => formatDate(row.created),
        id: 'created',
      },
    ],
    [history]
  );

  return (
    <Paper className={classes.paper}>
      <Stack direction='row' justifyContent='space-between' alignItems='center' sx={{ mb: 2 }}>
        <Typography variant='h5'>Small Business</Typography>
        <Typography variant='body2' color='text.secondary'>
          {total} total
        </Typography>
      </Stack>

      <Stack direction='row' spacing={2} sx={{ mb: 2 }}>
        <FormControl size='small' sx={{ minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select
            label='Status'
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value=''>All</MenuItem>
            <MenuItem value='active'>Active</MenuItem>
            <MenuItem value='past_due'>Past due</MenuItem>
            <MenuItem value='canceled'>Canceled</MenuItem>
            <MenuItem value='incomplete'>Incomplete</MenuItem>
          </Select>
        </FormControl>
        <FormControl size='small' sx={{ minWidth: 160 }}>
          <InputLabel>Tier</InputLabel>
          <Select
            label='Tier'
            value={tierFilter}
            onChange={(e) => setTierFilter(e.target.value)}
          >
            <MenuItem value=''>All</MenuItem>
            <MenuItem value='STARTER'>Starter</MenuItem>
            <MenuItem value='GROWTH'>Growth</MenuItem>
            <MenuItem value='PRO'>Pro</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <MaterialReactTable
          columns={columns}
          data={rows}
          enableColumnFilters={false}
          enableGlobalFilter={false}
          enableSorting
          enablePagination
          initialState={{ pagination: { pageIndex: 0, pageSize: 25 }, density: 'compact' }}
        />
      )}
    </Paper>
  );
}

export default withRouter(SmallBusinessLookup);
